<script setup>
import { computed, onMounted, reactive, ref } from 'vue';
import Button from 'dashboard/components-next/button/Button.vue';
import SettingsToggleSection from 'dashboard/components-next/Settings/SettingsToggleSection.vue';
import { useAlert } from 'dashboard/composables';
import { useMapGetter } from 'dashboard/composables/store';
import { useLocoI18n } from 'custom/composables/useLocoI18n';
import { useInboxSettings } from 'custom/conversation/useInboxSettings';

// Inbox settings › Replies: reply lock and the inbox signature
const props = defineProps({
  inbox: { type: Object, required: true },
});

const { t } = useLocoI18n();
const currentUser = useMapGetter('getCurrentUser');
const { fetchSettings, saveSettings } = useInboxSettings();

const VARIABLE_EXAMPLE = '{{agent.name}}';

const form = reactive({
  reply_lock: false,
  signature: { enabled: false, content: '', position: 'bottom' },
});
const loaded = ref(false);
const isSaving = ref(false);

// Rough preview: fills the most common variables the server renders with Liquid
const preview = computed(() => {
  const agentName = currentUser.value?.name || '';
  const signature = (form.signature.content || '')
    .replaceAll(/\{\{\s*agent\.name\s*\}\}/g, agentName)
    .replaceAll(/\{\{\s*agent\.first_name\s*\}\}/g, agentName.split(' ')[0])
    .replaceAll(/\{\{\s*inbox\.name\s*\}\}/g, props.inbox.name)
    .trim();
  const message = t('LOCO.INBOX_REPLIES.PREVIEW_MESSAGE');
  if (!signature) return message;
  return form.signature.position === 'top'
    ? `${signature}\n${message}`
    : `${message}\n\n${signature}`;
});

const save = async changes => {
  isSaving.value = true;
  try {
    const settings = await saveSettings(props.inbox.id, changes);
    Object.assign(form, structuredClone(settings));
    useAlert(t('LOCO.INBOX_REPLIES.SAVED'));
  } catch (error) {
    useAlert(error.response?.data?.error || error.message);
  } finally {
    isSaving.value = false;
  }
};

onMounted(async () => {
  const settings = await fetchSettings(props.inbox.id, { force: true });
  Object.assign(form, structuredClone(settings));
  loaded.value = true;
});
</script>

<template>
  <div v-if="loaded" class="flex flex-col gap-8 py-6">
    <SettingsToggleSection
      v-model="form.reply_lock"
      :header="t('LOCO.INBOX_REPLIES.LOCK')"
      :description="t('LOCO.INBOX_REPLIES.LOCK_DESC')"
      @update:model-value="value => save({ reply_lock: value })"
    />

    <section class="flex flex-col gap-4">
      <SettingsToggleSection
        v-model="form.signature.enabled"
        :header="t('LOCO.INBOX_REPLIES.SIGNATURE')"
        :description="t('LOCO.INBOX_REPLIES.SIGNATURE_DESC')"
        @update:model-value="value => save({ signature: { enabled: value } })"
      />

      <form
        v-if="form.signature.enabled"
        class="grid gap-4 md:grid-cols-2"
        @submit.prevent="save({ signature: form.signature })"
      >
        <div class="flex flex-col gap-3">
          <label class="flex flex-col gap-1 text-sm">
            <span class="font-medium text-n-slate-12">
              {{ t('LOCO.INBOX_REPLIES.CONTENT') }}
            </span>
            <textarea
              v-model="form.signature.content"
              rows="3"
              maxlength="500"
              class="!mb-0 resize-y"
            />
            <span class="text-xs text-n-slate-11">
              {{
                t('LOCO.INBOX_REPLIES.CONTENT_HELP', {
                  example: VARIABLE_EXAMPLE,
                })
              }}
            </span>
          </label>
          <label class="flex flex-col gap-1 text-sm">
            <span class="font-medium text-n-slate-12">
              {{ t('LOCO.INBOX_REPLIES.POSITION') }}
            </span>
            <select v-model="form.signature.position" class="!mb-0">
              <option value="bottom">
                {{ t('LOCO.INBOX_REPLIES.POSITION_BOTTOM') }}
              </option>
              <option value="top">
                {{ t('LOCO.INBOX_REPLIES.POSITION_TOP') }}
              </option>
            </select>
          </label>
          <div>
            <Button
              type="submit"
              sm
              :label="t('LOCO.INBOX_REPLIES.SAVE')"
              :is-loading="isSaving"
            />
          </div>
        </div>
        <div class="flex flex-col gap-1">
          <span class="text-sm font-medium text-n-slate-12">
            {{ t('LOCO.INBOX_REPLIES.PREVIEW') }}
          </span>
          <p
            class="p-3 rounded-xl ltr:rounded-br-sm rtl:rounded-bl-sm bg-n-solid-blue text-sm text-n-slate-12 whitespace-pre-wrap self-end max-w-sm"
          >
            {{ preview }}
          </p>
        </div>
      </form>
    </section>
  </div>
</template>
